import * as d3 from 'd3';
import { Block, ProcessGroup, Position, BlockGeometry, EditMode, Line, Layer } from '../types';
import { createBlock } from './blocks/BlockFactory';
import { BlockSelectionManager } from './blocks/BlockSelectionManager';
import { STYLES } from '../styles/constants';
import { snapToGrid } from '../utils/gridUtils';
import {
  ensureBlockDimensions,
  getBlockDimensions,
  getDecisionVisibleCharCap,
  getProcessLikeVisibleCharCap,
  getStartEndVisibleCharCap,
  splitDecisionTextIntoTwoLines,
} from '../utils/blockUtils';
import { applyContentFitTextStyles, getContentFitTextLines, isContentFitActive } from '../utils/blockTextLayout';
import { FamlaServices } from '../../../services';
import { getBpmnSymbol } from '../../data/bpmnSymbols';
import { isManualProcess, shouldSkipReferenceApi } from '../../../utils/processTypeUtils';
import {
  isManuallyAddedBlock as isManuallyAddedBlockUtil,
  isWarningEligibleBlock as isWarningEligibleBlockUtil,
} from '../utils/warningEligibility';
import { shouldDeferHeavyMapWork } from '../utils/renderOptimization';
import { STAKEHOLDER_CACHE_PERSIST } from '../utils/mapSwitchFlags';
import {
  getStakeholderCache,
  setStakeholderCache,
  hasStakeholderCacheForMap,
} from '../utils/stakeholderCacheStore';
import { markMapSwitchPhase } from '../utils/mapSwitchPerf';

const CHUNKED_RENDER_BLOCK_THRESHOLD = 15;
const CHUNKED_RENDER_BATCH_SIZE = 10;

export class ProcessManager {
  private container: d3.Selection<HTMLDivElement, unknown, null, undefined>;
  private blocks: Block[];
  private groups: ProcessGroup[];
  private editMode: EditMode;
  private onUpdate: (blocks: Block[], deletedBlockId?: string) => void;
  private onBlocksLiveUpdate?: (blocks: Block[]) => void;
  private onLineCreated?: (line: Line) => void;
  private onBlockSelect?: (block: Block | null) => void;
  private blockPositions: Map<string, Position>;
  public t: (key: string) => string;
  private selectionManager: BlockSelectionManager;
  private mapId: string;
  private setBlocks: React.Dispatch<React.SetStateAction<Block[]>>;
  private blockLayer: string[];
  private lineLayer: string[];
  private attributesDataEditable: any
  private lines: Line[];
  private laneHeaders: string[];
  private showWarnings: boolean;
  private showNotes: boolean;
  private stakeholderCache: Map<string, string[]> = new Map();
  private inFlightStakeholderRequests: Set<string> = new Set();
  private accessToken: string;
  private pendingNoteLinkId: string | null = null;
  private noteAttributePicker: d3.Selection<HTMLDivElement, unknown, null, undefined> | null = null;
  private layerVisibilityObserver: MutationObserver | null = null;
  private renderWarningsScheduled = false;
  private pendingWarningBlockIds: Set<string> | null = null;
  private layerSyncScheduled = false;
  private rendering = false;
  private suppressWarningsUntilMs = 0;
  private isSampleProcess = false;
  private processType: string;
  private destroyed = false;
  private onStakeholderVerificationLoading?: (loading: boolean) => void;
  private renderGeneration = 0;
  private chunkRenderHandle: ReturnType<typeof requestIdleCallback> | number | null = null;
  private isMapSwitchRender = false;

  /** Processus IMAGE ou HUMAN (fichier) : pas d'appels POST /reference/. */
  private isImageProcess(): boolean {
    return shouldSkipReferenceApi(this.processType);
  }

  private getCachedStakeholders(cacheKey: string): string[] | undefined {
    if (STAKEHOLDER_CACHE_PERSIST) {
      const fromStore = getStakeholderCache(cacheKey);
      if (fromStore) return fromStore;
    }
    return this.stakeholderCache.get(cacheKey);
  }

  private setCachedStakeholders(cacheKey: string, names: string[]): void {
    this.stakeholderCache.set(cacheKey, names);
    if (STAKEHOLDER_CACHE_PERSIST) {
      setStakeholderCache(cacheKey, names);
    }
  }

  private isManuallyAddedBlock(block: Block): boolean {
    return isManuallyAddedBlockUtil(block);
  }

  private isWarningEligibleBlock(block: Block): boolean {
    return isWarningEligibleBlockUtil(block, {
      processType: this.processType,
      isSampleProcess: this.isSampleProcess,
    });
  }

  private shouldSkipStakeholderMismatch(block: Block): boolean {
    return !this.isWarningEligibleBlock(block);
  }

  private shouldSkipStakeholderPreload(): boolean {
    if (this.isSampleProcess || this.isImageProcess() || isManualProcess(this.processType)) return true;
    if (STAKEHOLDER_CACHE_PERSIST && hasStakeholderCacheForMap(this.mapId)) return true;
    if (this.hasCompleteStakeholderCoverage()) return true;
    return false;
  }

  private hasCompleteStakeholderCoverage(): boolean {
    const eligible = this.blocks.filter(
      (block) => block.type !== 'start' && block.type !== 'end' && block.type !== 'note'
    );
    if (eligible.length === 0) return true;
    return eligible.every(
      (block) => this.getCachedStakeholders(this.stakeholderCacheKey(block)) !== undefined
    );
  }

  private cancelChunkRender(): void {
    this.renderGeneration += 1;
    if (this.chunkRenderHandle != null) {
      if (typeof this.chunkRenderHandle === 'number') {
        window.clearTimeout(this.chunkRenderHandle);
      } else if (typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(this.chunkRenderHandle);
      }
      this.chunkRenderHandle = null;
    }
  }

  constructor(
    container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
    blocks: Block[],
    groups: ProcessGroup[],
    editMode: EditMode,
    onUpdate: (blocks: Block[], deletedBlockId?: string) => void,
    t: (key: string) => string,
    setBlocks: React.Dispatch<React.SetStateAction<Block[]>>,
    blockLayer: string[],
    lineLayer: string[],
    attributesDataEditable: any,
    lines: Line[] = [],
    laneHeaders: string[] = [],
    showWarnings: boolean = true,
    isSampleProcess: boolean = false,
    onLineCreated?: (line: Line) => void,
    onBlockSelect?: (block: Block | null) => void,
    mapId?: string,
    accessToken?: string,
    stakeholderPreview?: Record<string, string[]>,
    processType?: string,
    onStakeholderVerificationLoading?: (loading: boolean) => void,
    onBlocksLiveUpdate?: (blocks: Block[]) => void
  ) {
    this.container = container;
    this.blocks = this.applyAttributeDisabledState(blocks.map(ensureBlockDimensions));
    this.groups = groups;
    this.editMode = editMode;
    this.mapId = mapId ?? "";
    this.onUpdate = onUpdate;
    this.onLineCreated = onLineCreated;
    this.onBlockSelect = onBlockSelect;
    this.blockPositions = new Map();
    this.t = t;
    this.setBlocks = setBlocks;
    this.blockLayer = blockLayer;
    this.lineLayer = lineLayer;
    this.attributesDataEditable = attributesDataEditable;
    this.lines = lines;
    this.laneHeaders = laneHeaders;
    this.showWarnings = showWarnings;
    this.showNotes = true;
    this.isSampleProcess = isSampleProcess;
    this.accessToken = accessToken || "";
    this.processType = (processType || '').toString().trim().toUpperCase();
    this.onStakeholderVerificationLoading = onStakeholderVerificationLoading;
    this.onBlocksLiveUpdate = onBlocksLiveUpdate;

    this.selectionManager = new BlockSelectionManager(
      container,
      {
        onDelete: this.handleDelete.bind(this),
        onAddLine: this.handleAddLine.bind(this),
        onLineCreated: this.handleLineCreated.bind(this),
        onLinkTask: this.handleStartNoteTaskLink.bind(this),
        onUnlinkTask: this.handleUnlinkNoteTask.bind(this),
        onLinkAttributes: this.handleLinkNoteAttributes.bind(this),
        onUnlinkAttributes: this.handleUnlinkNoteAttributes.bind(this),
        onAttachTextAnnotation: this.handleAttachTextAnnotation.bind(this),
      },
      mapId ?? "",
      t,
      lineLayer,
      this.accessToken,
      this.processType,
      (b) => {
        const key = this.stakeholderCacheKey(b);
        this.stakeholderCache.delete(key);
        this.inFlightStakeholderRequests.delete(key);
        if (this.showWarnings && !this.isSampleProcess) {
          this.scheduleRenderWarnings();
        }
      }
    );

    this.initializeBlockPositions();
    this.setupEventListeners();
    if (stakeholderPreview && typeof stakeholderPreview === 'object') {
      this.seedStakeholderPreview(stakeholderPreview);
    }
    this.render();
  }

  /** Invalide les requêtes /reference/ en cours avant recréation du diagramme (évite d’écraser l’état de chargement). */
  public markDestroyed(): void {
    this.destroyed = true;
    this.cancelChunkRender();
    this.inFlightStakeholderRequests.clear();
    if (this.layerVisibilityObserver) {
      this.layerVisibilityObserver.disconnect();
      this.layerVisibilityObserver = null;
    }
    this.container.on('swimlane-layer-hidden.processManager', null);
    this.container.on('swimlane-layer-shown.processManager', null);
    this.container.on('click.processManager-deselect', null);
  }

  /** Préremplit le cache /reference/ (réponse agrégée du backend) pour affichage immédiat des warnings. */
  public seedStakeholderPreview(preview: Record<string, string[]>): void {
    if (this.isSampleProcess || !preview) return;
    for (const block of this.blocks) {
      if (!block?.id || block.type === 'note' || block.type === 'start' || block.type === 'end') continue;
      if (!Object.prototype.hasOwnProperty.call(preview, block.id)) continue;
      const names = preview[block.id];
      const key = this.stakeholderCacheKey(block);
      this.setCachedStakeholders(
        key,
        Array.isArray(names)
          ? names.map((n) => String(n).trim()).filter(Boolean)
          : []
      );
    }
    if (this.showWarnings) {
      this.scheduleRenderWarnings();
    }
  }

  private initializeBlockPositions(): void {
    this.blockPositions.clear();
    this.blocks.forEach(block => {
      this.blockPositions.set(block.id, {
        x: block.x || 0,
        y: block.y || 0
      });
    });
    this.selectionManager.updateBlocks(this.blocks);
  }

  private setupEventListeners(): void {
    this.setupLayerVisibilityObserver();

    this.container.on('swimlane-layer-hidden.processManager', () => {
      this.scheduleLayerDependentSync();
    });
    this.container.on('swimlane-layer-shown.processManager', () => {
      this.scheduleLayerDependentSync();
    });

    if (this.editMode.enabled) {
      this.container.on('click.processManager-deselect', (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const isBlock = target.closest('[class*="-block-container-"], .note-block');
        const isMenu = target.closest('[class^="block-selection-menu"]');
        const isConnectionPoint = target.closest('.connection-point');
        const isModal = target.closest('.info-modal');
        const isAttributePicker = target.closest('.note-attribute-picker');

        if (!isBlock && !isMenu && !isConnectionPoint && !isModal && !isAttributePicker) {
          this.selectionManager.clearSelection();
          this.closeNoteAttributePicker();
          this.pendingNoteLinkId = null;
          this.container.selectAll('.block-selected')
            .classed('block-selected', false)
            .style('box-shadow', 'none');
          this.setBlocks((prevBlocks) =>
            prevBlocks.map((block) => ({ ...block, selected: false }))
          );
          if (this.onBlockSelect) {
            this.onBlockSelect(null);
          }
        }
      });
    }
  }

  private handleBlocksLiveMove = (updates: Array<{ id: string; position: Position }>): void => {
    if (updates.length === 0) return;

    updates.forEach(({ id, position }) => {
      const block = this.blocks.find((item) => item.id === id);
      if (!block) return;
      block.x = position.x;
      block.y = position.y;
      this.blockPositions.set(id, position);
      this.selectionManager.syncConnectionPointsForBlock(block);
    });

    this.onBlocksLiveUpdate?.([...this.blocks]);
    this.applyNotesVisibility();
  };

  private handleBlocksLiveResize = (id: string, geometry: BlockGeometry): void => {
    const block = this.blocks.find((item) => item.id === id);
    if (!block) return;

    if (geometry.x != null) block.x = geometry.x;
    if (geometry.y != null) block.y = geometry.y;
    if (geometry.width != null) block.width = geometry.width;
    if (geometry.height != null) block.height = geometry.height;
    this.blockPositions.set(id, { x: block.x || 0, y: block.y || 0 });
    this.selectionManager.syncConnectionPointsForBlock(block);
    this.onBlocksLiveUpdate?.([...this.blocks]);
  };

  private handleBlockMove = (id: string, position: BlockGeometry): void => {
    const block = this.blocks.find(b => b.id === id);
    if (block) {
      const isResize = position.width != null && position.height != null;
      const snappedPosition = isResize ? position : snapToGrid(position);
      let updatedBlock: Block = {
        ...block,
        x: snappedPosition.x ?? block.x,
        y: snappedPosition.y ?? block.y,
        ...(position.width != null ? { width: position.width } : {}),
        ...(position.height != null ? { height: position.height } : {}),
      };

      // Link notes to a task when dropped on top of a non-note block.
      if (updatedBlock.type === 'note') {
        const noteWithDimensions = ensureBlockDimensions(updatedBlock);
        const noteX = noteWithDimensions.x || 0;
        const noteY = noteWithDimensions.y || 0;
        const noteW = noteWithDimensions.width || STYLES.note.width;
        const noteH = noteWithDimensions.height || STYLES.note.height;

        const targetBlock = this.blocks.find(candidate => {
          if (candidate.id === updatedBlock.id || candidate.type === 'note') return false;
          const target = ensureBlockDimensions(candidate);
          const x = target.x || 0;
          const y = target.y || 0;
          const width = target.width || 0;
          const height = target.height || 0;
          // Match when rectangles intersect (more tolerant than center-point only)
          return noteX < x + width && noteX + noteW > x && noteY < y + height && noteY + noteH > y;
        });

        if (targetBlock) {
          updatedBlock = {
            ...updatedBlock,
            linkedTaskId: targetBlock.id
          };

          this.updateNoteBadge(updatedBlock);
        }
      }

      this.blocks = this.blocks.map(b =>
        b.id === id ? updatedBlock : b
      );
      this.blockPositions.set(id, snappedPosition);

      const headerHeight = STYLES.header.height + STYLES.header.gap;
      const laneHeight = STYLES.header.laneHeight;
      const relativeY = position.y - headerHeight;
      const laneIndex = Math.floor(relativeY / laneHeight);

      if (laneIndex >= 0 && laneIndex < this.groups.length) {
        this.updateBlockLaneAssignment(updatedBlock, laneIndex);
      }

      this.onUpdate([...this.blocks]);
      this.scheduleRenderWarnings();
      this.applyNotesVisibility();
    }
  };

  /** Persiste une modification de layer (valeur / unité) → React state + autosave. */
  private handleLayerUpdate = (blockId: string, layerId: string, updates: Partial<Layer>): void => {
    this.blocks = this.blocks.map((block) => {
      if (block.id !== blockId || !block.layers) return block;
      return {
        ...block,
        layers: block.layers.map((layer) =>
          layer.id === layerId ? { ...layer, ...updates } : layer
        ),
      };
    });
    this.selectionManager.updateBlocks(this.blocks);
    this.onUpdate([...this.blocks]);
  };

  private handleTextUpdate = (id: string, newText: string): void => {
    const block = this.blocks.find(b => b.id === id);
    if (block) {
      const sliceText = (text: string, b: Block): string => {
        if (!text) return text;
        if (b.type === 'note') return text;
        let maxLength: number;
        if (b.type === 'decision') maxLength = getDecisionVisibleCharCap(b.textStyle?.fontSize, b);
        else if (b.type === 'start' || b.type === 'end') maxLength = getStartEndVisibleCharCap(b.textStyle?.fontSize, b);
        else maxLength = getProcessLikeVisibleCharCap(b.textStyle?.fontSize, b);
        if (text.length <= maxLength) {
          return text;
        }
        return text.slice(0, maxLength) + '...';
      };

      // Mise à jour directe du texte dans le DOM pour conserver le même rendu
      const blockContainer = this.container.select(`.${block.type}-block-container-${id}`);
      if (!blockContainer.empty()) {
        if (block.type === 'decision') {
          const textElement = blockContainer.select('svg text');
          if (!textElement.empty()) {
            const fontSize = block.textStyle?.fontSize || 12;
            const lineHeight = fontSize * 1.2;
            const textLines = isContentFitActive(block)
              ? getContentFitTextLines({ ...block, text: newText })
              : splitDecisionTextIntoTwoLines(
                  newText,
                  getDecisionVisibleCharCap(block.textStyle?.fontSize, block)
                );

            textElement.selectAll('*').remove();

            textLines.forEach((line, index) => {
              if (index === 0) {
                textElement.text(line || '');
              } else {
                textElement.append('tspan')
                  .attr('x', textElement.attr('x') || '0')
                  .attr('dy', lineHeight)
                  .attr('text-anchor', 'middle')
                  .text(line);
              }
            });
          }
        } else {
          const blockDiv = blockContainer.select(`.${block.type}-block-${id}`);
          if (!blockDiv.empty()) {
            const textElement = blockDiv.select('div').node() as HTMLElement;
            if (textElement) {
              if (isContentFitActive(block)) {
                textElement.textContent = newText || '';
                applyContentFitTextStyles(textElement);
              } else {
                const displayedText = sliceText(newText, block);
                textElement.textContent = displayedText || '';
              }
            }
          }
        }
      }

      // Mise à jour de l'objet block dans le state avec le texte complet (pour la sauvegarde)
      const updatedBlock = { ...block, text: newText };
      this.blocks = this.blocks.map(b =>
        b.id === id ? updatedBlock : b
      );
      
      // Notifier la mise à jour sans déclencher de re-rendu complet
      // Le texte a déjà été mis à jour directement dans le DOM avec la même troncature
      this.onUpdate([...this.blocks]);
      this.scheduleRenderWarnings();
    }
  };

  private handleStartNoteTaskLink(note: Block): void {
    this.pendingNoteLinkId = note.id;
  }

  private handleUnlinkNoteTask(note: Block): void {
    const noteId = (note as any)?.id;
    if (!noteId) return;
    const currentNote = this.blocks.find((block) => block.id === noteId && block.type === 'note');
    if (!currentNote) return;

    this.blocks = this.applyAttributeDisabledState(
      this.blocks.map((block) => {
        if (block.id !== noteId || block.type !== 'note') return block;
        return {
          ...block,
          linkedTaskId: undefined,
          linkedAttributeIds: [],
          attributesDisabled: false,
        } as Block;
      })
    );
    const updatedNote = this.blocks.find((block) => block.id === noteId && block.type === 'note') as Block | undefined;
    if (!updatedNote) return;
    this.pendingNoteLinkId = null;
    this.closeNoteAttributePicker();
    this.updateNoteBadge(updatedNote);
    this.render();
    this.onUpdate([...this.blocks]);
    this.selectionManager.clearSelection();
    this.selectionManager.selectBlock(updatedNote);
  }

  private getAvailableBlockAttributeIds(note?: Block): string[] {
    const selectableByLayer = this.blockLayer ? new Set(this.blockLayer) : null;
    const editableLayers = Array.isArray(this.attributesDataEditable?.block_layers)
      ? this.attributesDataEditable.block_layers
      : [];
    const editableStartEndLayers = Array.isArray(this.attributesDataEditable?.start_end_layers)
      ? this.attributesDataEditable.start_end_layers
      : [];

    const fromEditable = editableLayers
      .filter((attr: any) => (selectableByLayer ? selectableByLayer.has(attr.id) : true))
      .map((attr: any) => attr.id);

    const baseAttrIds = fromEditable.length > 0
      ? fromEditable
      : (Array.isArray(this.blockLayer) && this.blockLayer.length > 0
        ? [...this.blockLayer]
        : ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']);

    // Business rule: note attributes come from the linked task attributes.
    if (note?.type === 'note' && note.linkedTaskId) {
      const allBlocks = Array.isArray(this.blocks) ? this.blocks : [];
      const task = allBlocks.find((b) => b.id === note.linkedTaskId && b.type !== 'note');
      const taskAttrIds = Array.isArray(task?.layers) ? task.layers.map((l: any) => l.id) : [];
      const isStartOrEnd = task?.type === 'start' || task?.type === 'end';

      // For start/end linked notes, do not fall back to process attributes.
      if (isStartOrEnd) {
        if (taskAttrIds.length > 0) {
          return taskAttrIds.map((id: any) => String(id));
        }
        if (editableStartEndLayers.length > 0) {
          return editableStartEndLayers.map((attr: any) => String(attr.id));
        }
        return ['1']; // default "Link" for start/end
      }

      if (taskAttrIds.length > 0) {
        const allowed = new Set(baseAttrIds);
        return taskAttrIds.filter((id: string) => allowed.has(id));
      }
    }

    return baseAttrIds;
  }

  private getAttributeLabelById(attributeId: string, linkedTaskId?: string): string {
    if (linkedTaskId) {
      const linkedTask = this.blocks.find((block) => block.id === linkedTaskId && block.type !== 'note');
      const linkedTaskLayer = linkedTask?.layers?.find((layer: any) => String(layer.id) === String(attributeId));
      if (linkedTaskLayer?.text) {
        const translationKey = `layer.block.${String(linkedTaskLayer.text).toLowerCase().replace(/\s+/g, '')}`;
        const translated = this.t(translationKey);
        return translated && translated !== translationKey ? translated : String(linkedTaskLayer.text);
      }
    }

    const labelsById: Record<string, string> = {
      '1': this.t('layer.block.leadtime'),
      '2': this.t('layer.block.defectrate'),
      '3': this.t('layer.block.itsystem'),
      '4': this.t('layer.block.cost'),
      '5': this.t('layer.block.carbonfootprint'),
      '6': this.t('layer.block.headcount'),
      '7': this.t('layer.block.irritantsandimprovements'),
      '8': this.t('layer.block.risksandcontrols'),
      '9': this.t('layer.block.relateddocuments'),
      '10': this.t('layer.block.failuremode'),
      '11': this.t('layer.block.wbs'),
      '12': this.t('layer.block.link'),
    };
    return labelsById[attributeId] || attributeId;
  }

  private closeNoteAttributePicker(): void {
    if (this.noteAttributePicker) {
      this.noteAttributePicker.remove();
      this.noteAttributePicker = null;
    }
  }

  private openNoteAttributePicker(note: Block, availableAttrIds: string[]): void {
    this.closeNoteAttributePicker();
    if (note.type !== 'note') return;

    const width = note.width || STYLES.note.width;
    const pickerMinWidth = 220;
    const pickerMaxWidth = 260;
    const pickerOffset = 10;
    const root = this.container.node();
    const containerWidth = root?.clientWidth || window.innerWidth;
    const noteX = note.x || 0;
    const rightPosition = noteX + width + pickerOffset;
    const leftPosition = noteX - pickerMaxWidth - pickerOffset;
    const shouldPlaceLeft = rightPosition + pickerMaxWidth > containerWidth && leftPosition >= 0;
    const computedLeft = shouldPlaceLeft
      ? leftPosition
      : Math.max(0, Math.min(rightPosition, containerWidth - pickerMinWidth));
    const linkedAttributeIds = new Set(note.linkedAttributeIds || []);

    this.noteAttributePicker = this.container
      .append('div')
      .attr('class', 'note-attribute-picker')
      .style('position', 'absolute')
      .style('left', `${computedLeft}px`)
      .style('top', `${note.y || 0}px`)
      .style('background', '#ffffff')
      .style('border', '1px solid #d1d5db')
      .style('border-radius', '8px')
      .style('box-shadow', '0 8px 24px rgba(0,0,0,0.16)')
      .style('padding', '8px')
      .style('z-index', '20')
      .style('min-width', `${pickerMinWidth}px`)
      .style('max-width', `${pickerMaxWidth}px`);

    this.noteAttributePicker
      .append('div')
      .style('font-size', '12px')
      .style('font-weight', '600')
      .style('margin-bottom', '6px')
      .text(this.t('features.swimlane.linkAttributesPrompt') || 'Choose attributes');

    const list = this.noteAttributePicker
      .append('div')
      .style('max-height', '160px')
      .style('overflow-y', 'auto')
      .style('display', 'grid')
      .style('gap', '0px')
      .style('margin-bottom', '8px');

    availableAttrIds.forEach((attrId) => {
      const label = list
        .append('label')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('gap', '6px')
        .style('font-size', '12px')
        .style('color', '#374151')
        .style('cursor', 'pointer');

      label
        .append('input')
        .attr('type', 'checkbox')
        .attr('data-attr-id', attrId)
        .property('checked', linkedAttributeIds.has(attrId));

      label
        .append('span')
        .text(this.getAttributeLabelById(String(attrId), note.linkedTaskId));
    });

    const actions = this.noteAttributePicker
      .append('div')
      .style('display', 'flex')
      .style('justify-content', 'flex-end')
      .style('gap', '6px');

    actions
      .append('button')
      .style('border', '1px solid #d1d5db')
      .style('background', '#f9fafb')
      .style('color', '#374151')
      .style('border-radius', '6px')
      .style('padding', '3px 8px')
      .style('font-size', '12px')
      .text(this.t('words.cancel') || 'Cancel')
      .on('click', (event: MouseEvent) => {
        event.stopPropagation();
        this.closeNoteAttributePicker();
      });

    actions
      .append('button')
      .style('border', '1px solid #2563eb')
      .style('background', '#2563eb')
      .style('color', '#ffffff')
      .style('border-radius', '6px')
      .style('padding', '3px 8px')
      .style('font-size', '12px')
      .text(this.t('words.save') || 'Save')
      .on('click', (event: MouseEvent) => {
        event.stopPropagation();
        if (!this.noteAttributePicker) return;

        const selected: string[] = [];
        this.noteAttributePicker.selectAll<HTMLInputElement, unknown>('input[type="checkbox"]').nodes().forEach((checkbox: HTMLInputElement) => {

          if (checkbox.checked) {
            const attrId = checkbox.getAttribute('data-attr-id');
            if (attrId) selected.push(attrId);
          }
        });

        const updatedNote: Block = {
          ...note,
          linkedAttributeIds: selected,
        } as Block;
        (updatedNote as any).attributesDisabled = this.isNoteDisabledByAttributes(updatedNote);

        this.blocks = this.applyAttributeDisabledState(
          this.blocks.map((b) => (b.id === note.id ? updatedNote : b))
        );
        this.updateNoteBadge(updatedNote);
        this.onUpdate([...this.blocks]);
        this.selectionManager.clearSelection();
        this.selectionManager.selectBlock(updatedNote);
        this.closeNoteAttributePicker();
      });
  }

  private getActiveBlockAttributeIds(): string[] {
    const selectableByLayer = this.blockLayer ? new Set(this.blockLayer) : null;
    const editableLayers = Array.isArray(this.attributesDataEditable?.block_layers)
      ? this.attributesDataEditable.block_layers
      : [];

    return editableLayers
      .filter((attr: any) => (selectableByLayer ? selectableByLayer.has(attr.id) : true))
      .filter((attr: any) => attr.edit !== false)
      .map((attr: any) => attr.id);
  }

  private isNoteDisabledByAttributes(note: Block): boolean {
    if (note.type !== 'note') return false;

    const linkedAttrs = Array.isArray((note as any).linkedAttributeIds)
      ? (note as any).linkedAttributeIds.map((id: any) => String(id))
      : [];
    if (linkedAttrs.length === 0) return false;

    const editableLayers = Array.isArray(this.attributesDataEditable?.block_layers)
      ? this.attributesDataEditable.block_layers
      : null;
    const selectedBlockLayer = Array.isArray(this.blockLayer)
      ? this.blockLayer.map((id: any) => String(id))
      : null;

    let activeIds = new Set<string>();

    if (editableLayers && editableLayers.length > 0) {
      const editableActive = editableLayers
        .filter((attr: any) => attr?.edit !== false)
        .map((attr: any) => String(attr.id));

      if (selectedBlockLayer && selectedBlockLayer.length > 0) {
        const selectedSet = new Set(selectedBlockLayer);
        activeIds = new Set(editableActive.filter((id: string) => selectedSet.has(id)));
      } else {
        activeIds = new Set(editableActive);
      }
    } else if (selectedBlockLayer) {
      activeIds = new Set(selectedBlockLayer);
    } else {
      // Config not loaded yet: avoid hiding prematurely.
      return false;
    }

    const hiddenOnLinkedTask = new Set<string>();
    if (note.linkedTaskId) {
      this.container
        .selectAll<HTMLElement, unknown>(`.layer-item[data-block-id="${note.linkedTaskId}"][data-layer-id]`)
        .each((_, index, nodes) => {
          const node = nodes[index] as HTMLElement;
          const layerId = node.getAttribute('data-layer-id');
          if (!layerId) return;
          if (node.style.display === 'none') {
            hiddenOnLinkedTask.add(String(layerId));
          }
        });
    }

    // Hide when every linked attribute is inactive either globally (Process Owner)
    // or locally hidden on the linked task layer list.
    return linkedAttrs.every((attrId: string) => !activeIds.has(attrId) || hiddenOnLinkedTask.has(attrId));
  }

  private applyAttributeDisabledState(blocks: Block[]): Block[] {
    return blocks.map((block) => {
      if (block.type !== 'note') return block;
      return {
        ...block,
        attributesDisabled: this.isNoteDisabledByAttributes(block),
      } as Block;
    });
  }

  private handleLinkNoteAttributes(note: Block): void {
    if (note.type !== 'note') return;
    if (!note.linkedTaskId) {
      return;
    }
    const availableAttrIds = this.getAvailableBlockAttributeIds(note);
    if (availableAttrIds.length === 0) return;
    this.openNoteAttributePicker(note, availableAttrIds);
  }

  private handleUnlinkNoteAttributes(note: Block): void {
    if (note.type !== 'note') return;
    const updatedNote: Block = {
      ...note,
      linkedAttributeIds: [],
      attributesDisabled: false,
    } as Block;

    this.blocks = this.applyAttributeDisabledState(
      this.blocks.map((b) => (b.id === note.id ? updatedNote : b))
    );
    this.updateNoteBadge(updatedNote);
    this.onUpdate([...this.blocks]);
    this.selectionManager.clearSelection();
    this.selectionManager.selectBlock(updatedNote);
    this.closeNoteAttributePicker();
  }
  private linkPendingNoteToTask(task: Block): boolean {
    if (!this.pendingNoteLinkId || task.type === 'note') return false;
    const note = this.blocks.find(b => b.id === this.pendingNoteLinkId && b.type === 'note');
    if (!note) return false;
    const updatedNote: Block = { ...(note as any), linkedTaskId: task.id };
    this.blocks = this.blocks.map(b => b.id === note.id ? updatedNote : b);
    this.pendingNoteLinkId = null;
    this.updateNoteBadge(updatedNote);
    this.onUpdate([...this.blocks]);
    this.selectionManager.clearSelection();
    this.selectionManager.selectBlock(updatedNote);
    return true;
  }

  private updateNoteBadge(note: Block): void {
    const noteId = (note as any)?.id;
    if (!noteId) return;
    const currentNote = this.blocks.find((block) => block.id === noteId && block.type === 'note') as Block | undefined;
    if (!currentNote || currentNote.type !== 'note') return;

    const blockContainer = this.container.select(`.note-block-container-${currentNote.id}`);
    if (blockContainer.empty()) return;
    blockContainer.select('.note-linked-badges').remove();
    blockContainer.select('.note-linked-badge').remove();
    blockContainer.select('.note-linked-attrs-badge').remove();

    // BPMN / DMN / VSM notation glyphs should not show sticky-note link / attribute captions.
    if (currentNote.bpmnSymbol || currentNote.dmnSymbol || currentNote.vsmSymbol) {
      if (!this.showNotes) {
        blockContainer.style('display', 'none');
      }
      return;
    }

    const allBlocks = Array.isArray(this.blocks) ? this.blocks : [];
    const target = currentNote.linkedTaskId
      ? allBlocks.find((b) => b.id === currentNote.linkedTaskId && b.type !== 'note')
      : undefined;
    const linkedName = target?.text || currentNote.linkedTaskId || (this.t('words.none') || 'None');
    const width = currentNote.width || STYLES.note.width;
    const height = currentNote.height || STYLES.note.height;

    const badgesContainer = blockContainer
      .append('div')
      .attr('class', 'note-linked-badges')
      .style('position', 'absolute')
      .style('left', '0px')
      .style('top', `${height + 4}px`)
      .style('display', 'flex')
      .style('flex-direction', 'column')
      .style('gap', '0px')
      .style('width', `${width}px`)
      .style('z-index', '3')
      .style('pointer-events', 'none');

    badgesContainer
      .append('div')
      .attr('class', 'note-linked-badge')
      .style('font-size', '10px')
      .style('line-height', '1.2')
      .style('color', '#4b5563')
      .style('background', 'rgba(255,255,255,0.95)')
      .style('border', '1px solid #d1d5db')
      .style('padding', '2px 4px')
      .style('border-radius', '3px')
      .style('max-width', '100%')
      .style('white-space', 'normal')
      .style('overflow', 'hidden')
      .style('display', '-webkit-box')
      .style('-webkit-line-clamp', '2')
      .style('-webkit-box-orient', 'vertical')
      .text(`${this.t('words.linkedTo')}: ${linkedName}`);

    const linkedAttributeIds = Array.isArray((currentNote as any).linkedAttributeIds)
      ? (currentNote as any).linkedAttributeIds
      : [];
    const linkedAttributeLabels = linkedAttributeIds.map((id: string) => this.getAttributeLabelById(id, currentNote.linkedTaskId));
    const maxVisibleAttributes = 2;
    const visibleAttributes = linkedAttributeLabels.slice(0, maxVisibleAttributes);
    const remainingCount = Math.max(0, linkedAttributeLabels.length - maxVisibleAttributes);
    const linkedAttributesLabel = linkedAttributeLabels.length > 0
      ? visibleAttributes.join(', ') + (remainingCount > 0 ? ' +' + remainingCount : '')
      : (this.t('words.none') || 'None');

    badgesContainer
      .append('div')
      .attr('class', 'note-linked-attrs-badge')
      .style('font-size', '10px')
      .style('line-height', '1.2')
      .style('color', '#4b5563')
      .style('background', 'rgba(255,255,255,0.95)')
      .style('border', '1px solid #d1d5db')
      .style('padding', '2px 4px')
      .style('border-radius', '3px')
      .style('max-width', '100%')
      .style('white-space', 'normal')
      .style('overflow', 'hidden')
      .style('display', '-webkit-box')
      .style('-webkit-line-clamp', '2')
      .style('-webkit-box-orient', 'vertical')
      .text(`${this.t('words.linkedAttributes') || 'Linked attributes'}:\n${linkedAttributesLabel}`);

    if (!this.showNotes) {
      blockContainer.style('display', 'none');
    }
  }

  private handleDelete = (block: Block): void => {
    this.deleteBlocks([block.id]);
  };

  public getSelectedBlock(): Block | null {
    return this.selectionManager.getSelectedBlock();
  }

  public collectSelectedBlockIds(): string[] {
    const ids = new Set<string>();
    const selected = this.selectionManager.getSelectedBlock();
    if (selected) ids.add(selected.id);

    this.blocks.forEach((block) => {
      if (block.selected) ids.add(block.id);
    });

    this.container.selectAll('.block-selected').each(function () {
      const el = this as HTMLElement;
      const fromAttr =
        el.getAttribute('data-block-id') ||
        el.closest('[data-block-id]')?.getAttribute('data-block-id');
      if (fromAttr) ids.add(fromAttr);
    });

    this.container.selectAll('[class*="-block-container-"]').each(function () {
      const el = this as HTMLElement;
      if (!el.classList.contains('block-selected') && !el.querySelector('.block-selected')) {
        return;
      }
      const cls = Array.from(el.classList).find((name) => name.includes('-block-container-'));
      if (!cls) return;
      const id = cls.replace(/^(process|decision|start|end|note)-block-container-/, '');
      if (id) ids.add(id);
    });

    return [...ids].filter((id) => this.blocks.some((block) => block.id === id));
  }

  private getSelectedBlockIds = (): string[] => this.collectSelectedBlockIds();

  public deleteBlocks(ids: string[]): void {
    const unique = [...new Set(ids)].filter((id) => this.blocks.some((block) => block.id === id));
    if (unique.length === 0) return;

    const deleted = new Set(unique);
    unique.forEach((id) => {
      const block = this.blocks.find((candidate) => candidate.id === id);
      if (!block) return;
      this.removeBlockFromCanvas(block);
    });

    this.lines = (this.lines || []).filter((line) => {
      const source = line.sourceId || line.from;
      const target = line.targetId || line.to;
      return !deleted.has(source || '') && !deleted.has(target || '');
    });

    this.selectionManager.clearSelection();
    this.selectionManager.updateBlocks(this.blocks);
    this.onUpdate([...this.blocks], unique[0]);
  }

  /** Retire la sélection et les poignées avant capture PNG/PDF. */
  public prepareForExport(): void {
    if (this.destroyed) return;
    this.selectionManager.clearSelection();
    this.container
      .selectAll('.block-selected')
      .classed('block-selected', false)
      .style('box-shadow', 'none')
      .style('outline', 'none');
    this.container
      .selectAll('[class*="-block-container-"]')
      .style('box-shadow', 'none');
    this.container.selectAll('[class^="block-selection-menu-"]').remove();
    this.container.selectAll('.block-resize-handle').remove();
    this.container.selectAll('.connection-point, [class*="connection-point"]').remove();
  }

  /** Append new blocks to the canvas without tearing down existing DOM (drag-drop, paste). */
  public insertBlocks(newBlocks: Block[]): void {
    if (this.destroyed || newBlocks.length === 0) return;

    const headerHeight = STYLES.header.height + STYLES.header.gap;
    const laneHeight = STYLES.header.laneHeight;
    const addedIds = new Set<string>();

    newBlocks.forEach((raw) => {
      const existing = this.blocks.some((block) => block.id === raw.id);
      if (existing) return;

      const block = this.applyAttributeDisabledState([ensureBlockDimensions(raw)])[0];
      if (block.type === 'note' && block.attributesDisabled) return;

      this.blocks.push(block);
      addedIds.add(block.id);
      this.blockPositions.set(block.id, { x: block.x || 0, y: block.y || 0 });

      const relativeY = (block.y || 0) - headerHeight;
      const laneIndex = Math.floor(relativeY / laneHeight);
      this.updateBlockLaneAssignment(block, laneIndex);

      this.mountBlock(block);
    });

    if (addedIds.size === 0) return;

    this.selectionManager.updateBlocks(this.blocks);
    this.suppressWarningsUntilMs = Date.now() + 500;
    this.scheduleRenderWarnings(addedIds);
    this.applyNotesVisibility();
  }

  /** Lightweight layer toggle — never call full render() (was causing observer loops / tab crashes). */
  private scheduleLayerDependentSync(): void {
    if (this.layerSyncScheduled || this.destroyed) return;
    this.layerSyncScheduled = true;
    requestAnimationFrame(() => {
      this.layerSyncScheduled = false;
      this.syncLayerDependentBlocks();
    });
  }

  private syncLayerDependentBlocks(): void {
    if (this.destroyed) return;

    const previousDisabled = new Map(
      this.blocks
        .filter((block) => block.type === 'note')
        .map((block) => [block.id, Boolean((block as any).attributesDisabled)])
    );

    this.blocks = this.applyAttributeDisabledState(this.blocks);
    this.selectionManager.updateBlocks(this.blocks);

    this.blocks.forEach((block) => {
      if (block.type !== 'note') return;
      const wasDisabled = previousDisabled.get(block.id);
      const nowDisabled = Boolean((block as any).attributesDisabled);
      if (wasDisabled === nowDisabled) return;

      const containerSel = this.container.select(`.note-block-container-${block.id}`);
      if (nowDisabled) {
        this.container.select(`[class^="block-selection-menu-${block.id}"]`).remove();
        if (!containerSel.empty()) containerSel.remove();
        return;
      }
      if (containerSel.empty()) {
        this.mountBlock(block);
      }
    });

    this.applyNotesVisibility();
  }

  private mountBlock(block: Block): void {
    this.container.select(`.${block.type}-block-container-${block.id}`).remove();
    this.container.select(`[class^="block-selection-menu-${block.id}"]`).remove();

    const storedPosition = this.blockPositions.get(block.id);
    if (storedPosition) {
      block.x = storedPosition.x;
      block.y = storedPosition.y;
    }

    const blockElement = createBlock(
      this.container,
      block,
      this.editMode,
      this.handleBlockMove,
      this.handleTextUpdate,
      this.blocks,
      this.t,
      this.setBlocks,
      this.attributesDataEditable,
      this.handleLayerUpdate,
      this.handleBlocksLiveMove,
      this.getSelectedBlockIds,
      this.handleBlocksLiveResize
    );

    if (block.type === 'note') {
      this.updateNoteBadge(block);
    }

    this.applyNotesVisibility();

    if (blockElement && this.editMode.enabled) {
      blockElement
        .attr('data-block-id', block.id)
        .on('mousedown.select', (event) => {
          const mouseEvent = event as MouseEvent;
          if (mouseEvent.button !== 0) return;
          const target = mouseEvent.target as HTMLElement;
          if (target.closest('.block-resize-handle')) return;

          const latestBlock = this.blocks.find((item) => item.id === block.id);
          if (!latestBlock) return;
          if (!mouseEvent.ctrlKey && !mouseEvent.metaKey) {
            const selectedIds = this.collectSelectedBlockIds();
            const isMultiDragTarget = selectedIds.length > 1 && selectedIds.includes(block.id);
            if (!isMultiDragTarget) {
              this.container.selectAll('.block-selected')
                .classed('block-selected', false)
                .style('box-shadow', 'none');
              blockElement.classed('block-selected', true);
              this.setBlocks((prevBlocks) =>
                prevBlocks.map((item) => ({
                  ...item,
                  selected: item.id === block.id,
                }))
              );
              this.selectionManager.selectBlock(latestBlock);
              if (this.onBlockSelect) {
                this.onBlockSelect(latestBlock);
              }
            }
          }
        })
        .on('click.processManager', (event) => {
          const mouseEvent = event as MouseEvent;
          const multiSelect = mouseEvent.ctrlKey || mouseEvent.metaKey;

          setTimeout(() => {
            const latestBlock = this.blocks.find((b) => b.id === block.id);
            if (!latestBlock) return;

            if (!multiSelect) {
              if (this.pendingNoteLinkId && latestBlock.type !== 'note') {
                this.linkPendingNoteToTask(latestBlock);
              }
              this.selectionManager.selectBlock(latestBlock);

              if (this.onBlockSelect) {
                this.onBlockSelect(latestBlock);
              }
            } else {
              this.selectionManager.clearSelection();
            }

            if (latestBlock.type !== 'note' || latestBlock.bpmnSymbol === 'textAnnotation') {
              this.handleAddLine(latestBlock);
            }
          }, 0);
        });
    }
  }

  private removeBlockFromCanvas(block: Block): void {
    this.blocks = this.blocks.filter((b) => b.id !== block.id);
    this.blockPositions.delete(block.id);
    this.groups.forEach((group) => {
      group.groupChildren = group.groupChildren.filter((childId) => childId !== block.id);
    });

    this.container.selectAll(`[class^="block-selection-menu-${block.id}"]`).remove();
    this.container.selectAll(`.connection-point[data-block-id="${block.id}"]`).remove();
    this.container.select(`.${block.type}-block-container-${block.id}`).remove();
    this.container.selectAll(`div[class*="-block-container-${block.id}"]`).remove();
    this.container.selectAll(`[data-block-id="${block.id}"]`).remove();
    this.container.select(`[id="${block.id}"]`).remove();
  }

  private handleAddLine = (block: Block): void => {
    // Start the link mode in the selection manager
    this.selectionManager.startLinkMode(block);
  };

  private handleAttachTextAnnotation = (block: Block): void => {
    if (block.type === 'note') return;

    const sourceDims = getBlockDimensions(block);
    const annotationWidth = 140;
    const annotationHeight = 50;
    const existingCount = this.blocks.filter(
      (item) =>
        item.type === 'note' &&
        item.bpmnSymbol === 'textAnnotation' &&
        item.linkedTaskId === block.id
    ).length;
    // Place on a diagonal axis (down-right), offset each additional annotation.
    const step = 36;
    const gapX = 48;
    const gapY = 28;
    const annotationId = `note-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const annotationX = (block.x || 0) + sourceDims.width + gapX + existingCount * step;
    const annotationY = (block.y || 0) + sourceDims.height + gapY + existingCount * step;

    const annotation: Block = {
      id: annotationId,
      type: 'note',
      text: getBpmnSymbol('textAnnotation')?.defaultText ?? 'text annotation',
      x: annotationX,
      y: annotationY,
      width: annotationWidth,
      height: annotationHeight,
      bpmnSymbol: 'textAnnotation',
      linkedTaskId: block.id,
      meta: { source: 'palette' },
    };

    const association: Line = {
      id: `association-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      type: 'association',
      sourceId: '',
      targetId: '',
      from: block.id,
      to: annotationId,
      fromSide: 'right',
      toSide: 'left',
      text: '',
    };

    this.insertBlocks([annotation]);
    // Keep SwimlaneHeaders block list in sync before line create (which may re-render).
    this.onUpdate([...this.blocks]);
    this.handleLineCreated(association);
    this.selectionManager.selectBlock(annotation);
    if (this.onBlockSelect) {
      this.onBlockSelect(annotation);
    }
  };

  private handleLineCreated = (line: Line): void => {
    if (this.onLineCreated) {
      this.onLineCreated(line);
    }
  };

  private updateBlockLaneAssignment(block: Block, newLaneIndex: number): void {
    // Remove block from all groups first
    this.groups.forEach(group => {
      group.groupChildren = group.groupChildren.filter(id => id !== block.id);
    });

    // Add block to new group if valid
    if (newLaneIndex >= 0 && newLaneIndex < this.groups.length) {
      this.groups[newLaneIndex].groupChildren.push(block.id);
    }
  }

  public swapSheet(
    blocks: Block[],
    lines: Line[],
    laneHeaders: string[],
    mapId: string,
    options?: { isMapSwitch?: boolean }
  ): void {
    this.cancelChunkRender();
    this.mapId = mapId;
    this.selectionManager.setMapId(this.mapId);
    this.lines = lines;
    this.laneHeaders = laneHeaders;

    const nextIds = new Set(blocks.map((b) => b.id));
    this.container
      .selectAll('[class^="block-selection-menu-"]')
      .each(function () {
        const el = this as HTMLElement;
        const match = Array.from(el.classList).find((c) => c.startsWith('block-selection-menu-'));
        if (!match) return;
        const blockId = match.replace('block-selection-menu-', '');
        if (!nextIds.has(blockId)) d3.select(el).remove();
      });

    this.container
      .selectAll(
        '.process-block, .decision-block, .start-block, .end-block, .note-block,' +
        'div[class*="-block-container-"], div[class^="note-block-container-"],' +
        '.note-linked-badges, .note-linked-badge, .note-linked-attrs-badge'
      )
      .remove();

    this.blocks = this.applyAttributeDisabledState(blocks.map(ensureBlockDimensions));
    this.initializeBlockPositions();
    this.isMapSwitchRender = options?.isMapSwitch ?? true;
    markMapSwitchPhase('process_manager_swap');
    this.render();
    this.isMapSwitchRender = false;
    this.suppressWarningsUntilMs = Date.now() + 140;
    this.scheduleRenderWarnings();
  }

  public updateData(blocks: Block[]): void {
    // Full cleanup to avoid stale containers/badges/menus after reload or remote updates.
    this.container
      .selectAll(
        '.process-block, .decision-block, .start-block, .end-block, .note-block,' +
        'div[class*="-block-container-"], div[class^="note-block-container-"],' +
        '[class^="block-selection-menu-"], .note-linked-badges, .note-linked-badge, .note-linked-attrs-badge'
      )
      .remove();

    // Update blocks array with new data
    this.blocks = this.applyAttributeDisabledState(blocks.map(ensureBlockDimensions));

    // Reinitialize positions and render
    this.initializeBlockPositions();
    this.render();
    this.preloadStakeholderVerification();
    // Suppress warning churn briefly while the DOM settles
    this.suppressWarningsUntilMs = Date.now() + 140;
    this.scheduleRenderWarnings();
  }

  public updateLines(lines: Line[]): void {
    this.lines = lines;
    this.suppressWarningsUntilMs = Date.now() + 100;
    this.scheduleRenderWarnings();
  }

  public setWarningsEnabled(enabled: boolean): void {
    // Never allow warnings for sample process
    const next = this.isSampleProcess ? false : enabled;
    this.showWarnings = next;
    if (next) {
      this.preloadStakeholderVerification();
      // Small delay avoids blink when toggling during map switches
      this.suppressWarningsUntilMs = Date.now() + 80;
      this.scheduleRenderWarnings();
    } else {
      // Immediately clear any existing badges/slots when hiding warnings
      this.container.selectAll('.block-warning-slot').remove();
      this.container.selectAll('.block-warning-badge').remove();
    }
  }

  public setNotesVisible(visible: boolean): void {
    this.showNotes = visible;
    if (!visible) {
      this.selectionManager.clearSelection();
    }
    this.applyNotesVisibility();
  }

  private applyNotesVisibility(): void {
    const display = this.showNotes ? 'block' : 'none';
    this.container
      .selectAll<HTMLElement, unknown>('div[class^="note-block-container-"]')
      .style('display', display);

    const noteIds = this.blocks.filter((block) => block.type === 'note').map((block) => block.id);
    noteIds.forEach((noteId) => {
      const menu = this.container.select(`[class^="block-selection-menu-${noteId}"]`);
      if (!menu.empty()) {
        menu.style('display', this.showNotes ? 'block' : 'none');
      }
    });
  }

  private scheduleRenderWarnings(onlyBlockIds?: Set<string>): void {
    if (onlyBlockIds && onlyBlockIds.size > 0) {
      if (!this.pendingWarningBlockIds) {
        this.pendingWarningBlockIds = new Set();
      }
      onlyBlockIds.forEach((id) => this.pendingWarningBlockIds!.add(id));
    }
    if (this.renderWarningsScheduled) return;
    this.renderWarningsScheduled = true;
    const invoke = () => {
      this.renderWarningsScheduled = false;
      const now = Date.now();
      if (now < this.suppressWarningsUntilMs) {
        const delay = Math.max(0, this.suppressWarningsUntilMs - now);
        setTimeout(() => this.scheduleRenderWarnings(), delay);
        return;
      }
      const blockIds = this.pendingWarningBlockIds;
      this.pendingWarningBlockIds = null;
      this.renderWarnings(blockIds ?? undefined);
    };
    requestAnimationFrame(() => requestAnimationFrame(invoke));
  }

  // Ensure badges are (re)drawn immediately (used for export snapshots)
  public forceRenderWarnings(): void {
    this.renderWarnings();
  }

  private normalizeStakeholderName(value: string): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private matchesStakeholder(expectedLane: string, stakeholder: string): boolean {
    const expected = this.normalizeStakeholderName(expectedLane);
    const actual = this.normalizeStakeholderName(stakeholder);
    if (!expected || !actual) return false;
    if (expected === actual) return true;
    if (expected.includes(actual) || actual.includes(expected)) return true;

    const aliases: Record<string, string[]> = {
      consumer: ['customer', 'client', 'consommateur', 'consommateur final'],
      analyst: ['analyste'],
      supplier: ['data supplier', 'fournisseur', 'fournisseur de donnees'],
    };

    const expectedAliases = aliases[expected] || [];
    const actualAliases = aliases[actual] || [];
    return expectedAliases.includes(actual) || actualAliases.includes(expected);
  }

  private getExpectedLaneForBlock(block: Block): string {
    const headerHeight = STYLES.header.height + STYLES.header.gap;
    const laneIndex = Math.floor(((block.y || 0) - headerHeight) / STYLES.header.laneHeight);
    return laneIndex >= 0 && laneIndex < this.laneHeaders.length ? this.laneHeaders[laneIndex] : '';
  }

  private getLineDegree(blockId: string): number {
    return (this.lines || []).filter((line) => line?.from === blockId || line?.to === blockId).length;
  }

  private getConnectionStats(blockId: string): { incoming: number; outgoing: number } {
    let incoming = 0;
    let outgoing = 0;

    (this.lines || []).forEach((line) => {
      const fromId = (line as any)?.from ?? (line as any)?.sourceId;
      const toId = (line as any)?.to ?? (line as any)?.targetId;
      if (fromId === blockId) outgoing += 1;
      if (toId === blockId) incoming += 1;
    });

    return { incoming, outgoing };
  }

  /** Clé de cache par feuille (mapId) pour réutiliser les résultats /reference/ entre onglets. */
  private stakeholderCacheKey(block: Block): string {
    // Omitting block.id ensures deduplication of identical blocks (e.g. stress test multiplication)
    return `${this.mapId}::${block.text || ''}`;
  }

  private notifyStakeholderInFlight(): void {
    if (this.destroyed) return;
    this.onStakeholderVerificationLoading?.(this.inFlightStakeholderRequests.size > 0);
  }

  private ensureStakeholderData(block: Block): Promise<void> {
    if (this.isSampleProcess) return Promise.resolve();
    if (this.isImageProcess()) return Promise.resolve();
    if (this.shouldSkipStakeholderMismatch(block)) return Promise.resolve();
    if (!this.mapId || !this.mapId.trim() || block.type === 'note') return Promise.resolve();
    const cacheKey = this.stakeholderCacheKey(block);
    if (this.getCachedStakeholders(cacheKey) || this.inFlightStakeholderRequests.has(cacheKey)) {
      return Promise.resolve();
    }
    this.inFlightStakeholderRequests.add(cacheKey);
    this.notifyStakeholderInFlight();

    return FamlaServices(
      'api_host',
      '/reference/',
      'POST',
      {
        map_id: this.mapId,
        text: block.text,
        element_id: block.id,
      },
      this.accessToken || ''
    )
      .then((response: any) => {
        const data =
          response?.body?.data ??
          response?.body ??
          response?.data?.data ??
          response?.data;
        const stakeholders = Array.isArray(data)
          ? data
            .map((item: any) => item?.stakeholder)
            .filter((name: any) => typeof name === 'string' && name.trim().length > 0)
          : [];
        this.setCachedStakeholders(cacheKey, stakeholders);
      })
      .catch(() => {
        this.setCachedStakeholders(cacheKey, []);
      })
      .finally(() => {
        this.inFlightStakeholderRequests.delete(cacheKey);
        if (this.destroyed) return;
        this.notifyStakeholderInFlight();
        if (this.showWarnings) {
          this.renderWarnings();
        }
      });
  }

  /** Appels /reference/ en arrière-plan : indépendant de l’affichage des pastilles pour remplir le cache au chargement. */
  private async preloadStakeholderVerification(): Promise<void> {
    if (this.shouldSkipStakeholderPreload()) return;
    markMapSwitchPhase('preload_reference');
    if (!this.mapId || !this.mapId.trim()) return;
    const eligibleBlocks = this.blocks.filter(
      (block) =>
        block.type !== 'start' &&
        block.type !== 'end' &&
        block.type !== 'note' &&
        !this.shouldSkipStakeholderMismatch(block)
    );
    
    // Traitement par lots (batches) pour éviter de spammer le réseau avec des milliers de requêtes
    const batchSize = 10;
    for (let i = 0; i < eligibleBlocks.length; i += batchSize) {
      if (this.destroyed) break;
      const batch = eligibleBlocks.slice(i, i + batchSize);
      await Promise.all(batch.map((block) => this.ensureStakeholderData(block)));
    }
  }

  private clearWarningBadge(block: Block): void {
    const blockContainer = this.container.select(`.${block.type}-block-container-${block.id}`);
    if (blockContainer.empty()) return;
    blockContainer.select('.block-warning-slot').remove();
  }

  private renderWarnings(onlyBlockIds?: Set<string>): void {
    if (!this.showWarnings) return;
    if (this.isSampleProcess) {
      // Hard guard: ensure nothing is shown and clear any stale elements
      this.container.selectAll('.block-warning-slot').remove();
      this.container.selectAll('.block-warning-badge').remove();
      return;
    }

    let eligibleBlocks = this.blocks.filter((block) => this.isWarningEligibleBlock(block));
    if (onlyBlockIds && onlyBlockIds.size > 0) {
      eligibleBlocks = eligibleBlocks.filter((block) => onlyBlockIds.has(block.id));
    }

    const eligibleIds = new Set(eligibleBlocks.map((block) => block.id));
    this.blocks.forEach((block) => {
      if (!eligibleIds.has(block.id)) {
        this.clearWarningBadge(block);
      }
    });

    eligibleBlocks.forEach((block) => {
      const connectionStats = this.getConnectionStats(block.id);
      const singleConnection = (connectionStats.incoming + connectionStats.outgoing) === 1;
      const oneSidedFlow = (
        (connectionStats.incoming > 0 && connectionStats.outgoing === 0) ||
        (connectionStats.outgoing > 0 && connectionStats.incoming === 0)
      );
      const expectedLane = this.getExpectedLaneForBlock(block);
      const cacheKey = this.stakeholderCacheKey(block);
      const stakeholders = this.getCachedStakeholders(cacheKey);
      const skipStakeholderMismatch = this.shouldSkipStakeholderMismatch(block);
      const stakeholderMismatch = !skipStakeholderMismatch && Boolean(
        expectedLane &&
        Array.isArray(stakeholders) &&
        stakeholders.length > 0 &&
        stakeholders
          .filter((name) => this.normalizeStakeholderName(name) !== 'process owner')
          .every((name) => !this.matchesStakeholder(expectedLane, name))
      );

      if (!skipStakeholderMismatch && !stakeholders) {
        this.ensureStakeholderData(block);
      }

      const shouldWarn = singleConnection || oneSidedFlow || stakeholderMismatch;
      if (!shouldWarn) {
        this.clearWarningBadge(block);
        return;
      }

      const blockContainer = this.container.select(`.${block.type}-block-container-${block.id}`);
      if (blockContainer.empty()) return;

      // Ensure a persistent slot to avoid remove→add flicker
      const slot = blockContainer.select<HTMLDivElement>('.block-warning-slot');
      const ensuredSlot = slot.empty()
        ? blockContainer
            .append('div')
            .attr('class', 'block-warning-slot')
            .style('position', 'absolute')
            .style('top', '-16px')
            .style('right', '-16px')
            .style('width', '24px')
            .style('height', '24px')
            .style('z-index', '13')
        : slot;

      const reasons: string[] = [];
      if (singleConnection) reasons.push(this.t('features.swimlane.warningSingleConnection'));
      if (oneSidedFlow) reasons.push(this.t('features.swimlane.warningOneSidedFlow'));
      if (stakeholderMismatch) reasons.push(this.t('features.swimlane.warningStakeholderMismatch'));

      // Update slot content atomically to avoid flicker
      if (reasons.length === 0) {
        ensuredSlot.html('');
      } else {
        ensuredSlot
          .html('') // clear previous
          .attr('title', reasons.join(' | '));

        ensuredSlot
          .append('div')
          .attr('class', 'block-warning-badge')
          .style('position', 'absolute')
          .style('left', '0px')
          .style('top', '0px')
          .style('width', '24px')
          .style('height', '24px')
          .style('filter', 'drop-shadow(0 1px 1px rgba(0,0,0,0.35))')
          .style('pointer-events', 'auto');

        ensuredSlot
          .append('div')
          .style('position', 'absolute')
          .style('left', '0px')
          .style('top', '0px')
          .style('width', '0')
          .style('height', '0')
          .style('border-left', '12px solid transparent')
          .style('border-right', '12px solid transparent')
          .style('border-bottom', '#fde68a solid 24px');

        ensuredSlot
          .append('div')
          .style('position', 'absolute')
          .style('left', '50%')
          .style('top', '8px')
          .style('transform', 'translateX(-50%)')
          .style('color', '#111827')
          .style('font-size', '13px')
          .style('font-weight', '800')
          .style('line-height', '1')
          .style('pointer-events', 'none')
          .text('!');
      }
    });

    this.applyNotesVisibility();
  }

  private render(): void {
    if (this.destroyed || this.rendering) return;
    this.rendering = true;
    try {
      this.renderBlocks();
    } finally {
      this.rendering = false;
    }
  }

  private renderBlocks(): void {
    const visibleBlocks = this.applyAttributeDisabledState(this.blocks)
      .filter((block): block is Block => Boolean(block))
      .filter((block) => !(block.type === 'note' && block.attributesDisabled));
    const visibleNoteIds = new Set(
      visibleBlocks.filter((block) => block.type === 'note').map((block) => block.id)
    );

    this.container
      .selectAll<HTMLElement, unknown>('div[class^="note-block-container-"]')
      .each((_, index, nodes) => {
        const node = nodes[index] as HTMLElement;
        const classes = Array.from(node.classList);
        const containerClass = classes.find((className) => className.startsWith('note-block-container-'));
        if (!containerClass) return;
        const noteId = containerClass.replace('note-block-container-', '');
        if (visibleNoteIds.has(noteId)) return;
        this.container.select(`[class^="block-selection-menu-${noteId}"]`).remove();
        d3.select(node).remove();
      });

    const visibleIds = new Set(visibleBlocks.map((block) => block.id));
    this.container.selectAll<HTMLElement, unknown>('div[class*="-block-container-"]').each(function () {
      const node = this as HTMLElement;
      const cls = Array.from(node.classList).find((name) => name.includes('-block-container-'));
      if (!cls) return;
      const blockId = cls.replace(/^(process|decision|start|end|note)-block-container-/, '');
      if (blockId && !visibleIds.has(blockId)) {
        d3.select(node).remove();
      }
    });

    // Get all existing block elements
    const existingBlocks = this.container
      .selectAll<HTMLDivElement, Block>('.process-block, .decision-block, .start-block, .end-block, .note-block')
      .data(visibleBlocks as Block[], (d: any, i: number) => d?.id ?? `missing-block-${i}`);

    // Remove blocks that no longer exist in the data
    existingBlocks.exit().remove();

    // Update existing blocks
    existingBlocks
      .style('left', (d: any) => `${d?.x ?? 0}px`)
      .style('top', (d: any) => `${d?.y ?? 0}px`);

    // Keep note badges in sync with the source of truth after any data change/reload.
    visibleBlocks
      .filter((block) => block.type === 'note')
      .forEach((noteBlock) => this.updateNoteBadge(noteBlock));

    // Add new blocks (chunked on large map switches)
    const blocksToCreate: Block[] = [];
    visibleBlocks.forEach((block) => {
      const existingBlock = this.container.select(`.${block.type}-block-${block.id}`);
      if (!existingBlock.empty()) return;
      blocksToCreate.push(block);
    });

    const useChunkedRender =
      this.isMapSwitchRender &&
      blocksToCreate.length >= CHUNKED_RENDER_BLOCK_THRESHOLD;

    const createOneBlock = (block: Block) => {
      this.mountBlock(block);
    };

    const finishRenderPass = () => {
      if (this.destroyed) return;
      if (!this.shouldSkipStakeholderPreload()) {
        this.preloadStakeholderVerification();
      }
      this.renderWarnings();
      this.applyNotesVisibility();
    };

    if (useChunkedRender) {
      const generation = this.renderGeneration;
      let index = 0;

      const runBatch = () => {
        if (this.destroyed || generation !== this.renderGeneration) return;
        const end = Math.min(index + CHUNKED_RENDER_BATCH_SIZE, blocksToCreate.length);
        for (; index < end; index += 1) {
          createOneBlock(blocksToCreate[index]);
        }
        if (index < blocksToCreate.length) {
          const schedule =
            typeof requestIdleCallback === 'function'
              ? requestIdleCallback
              : (cb: () => void) => window.setTimeout(cb, 0);
          this.chunkRenderHandle = schedule(runBatch);
        } else {
          this.chunkRenderHandle = null;
          finishRenderPass();
        }
      };

      runBatch();
      return;
    }

    blocksToCreate.forEach((block) => createOneBlock(block));
    finishRenderPass();
  }

  private setupLayerVisibilityObserver(): void {
    const root = this.container.node();
    if (!root) return;

    if (this.layerVisibilityObserver) {
      this.layerVisibilityObserver.disconnect();
    }

    this.layerVisibilityObserver = new MutationObserver((mutations) => {
      const hasLayerVisibilityChange = mutations.some((mutation) => {
        if (mutation.type !== 'attributes') return false;
        const target = mutation.target as HTMLElement;
        return Boolean(target?.classList?.contains('layer-item'));
      });

      if (!hasLayerVisibilityChange) return;
      this.scheduleLayerDependentSync();
    });

    this.layerVisibilityObserver.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }

  /**
   * Update runtime context when switching maps/tabs to keep API calls and headers in sync.
   */
  public setMapContext(
    mapId: string,
    accessToken?: string,
    laneHeaders?: string[],
    lines?: Line[],
    isSampleProcess?: boolean,
    processType?: string
  ): void {
    this.mapId = mapId ?? this.mapId;
    this.selectionManager.setMapId(this.mapId);
    if (typeof accessToken === 'string') {
      this.accessToken = accessToken;
    }
    if (Array.isArray(laneHeaders)) {
      this.laneHeaders = laneHeaders;
    }
    if (Array.isArray(lines)) {
      this.lines = lines;
    }
    if (typeof isSampleProcess === 'boolean') {
      this.isSampleProcess = isSampleProcess;
    }
    if (typeof processType === 'string') {
      this.processType = processType.trim().toUpperCase();
      this.selectionManager.setProcessType(this.processType);
    }
    const deferHeavyWork = shouldDeferHeavyMapWork({
      isSampleProcess: this.isSampleProcess,
      showWarnings: this.showWarnings,
      blockCount: this.blocks.length,
      isMapSwitch: true,
    });

    // Pas de clear global : les clés incluent mapId — retour sur un onglet = cache /reference/ réutilisable
    if (!this.shouldSkipStakeholderPreload()) {
      if (deferHeavyWork) {
        window.setTimeout(() => {
          if (this.destroyed) return;
          this.preloadStakeholderVerification();
        }, 250);
      } else {
        this.preloadStakeholderVerification();
      }
    }
    if (this.showWarnings && !this.isSampleProcess) {
      if (deferHeavyWork) {
        window.setTimeout(() => {
          if (this.destroyed) return;
          this.scheduleRenderWarnings();
        }, 120);
      } else {
        this.scheduleRenderWarnings();
      }
    } else {
      this.container.selectAll('.block-warning-badge').remove();
      this.container.selectAll('.block-warning-slot').remove();
    }
  }
}































